import React, { useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { AppEventPublisher } from "src/AppEventPublisher";
import type { FeedbackPlanUpsellSource } from "src/AppEventPublisher";

export interface FeedbackPlanUpsellDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasActiveSubscription?: boolean;
  source?: FeedbackPlanUpsellSource;
  currentPlanTitle?: string | null;
}

const FEEDBACK_LEARN_MORE_URL = "https://sudowrite.com/features/feedback?utm_source=sudowrite_web_app";

const UpsellPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 30000;
  width: 426px;
  height: 526px;
  max-width: calc(100vw - 32px);
  border: 0;
  border-radius: 12px;
  background: transparent;
  overflow: hidden;
  box-sizing: border-box;
`;

const Visual = styled.img`
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 426px;
  height: 526px;
  max-width: none;
  pointer-events: none;
  user-select: none;
  display: none;
`;

const LightVisual = styled(Visual)`
  body:not([data-theme-type="dark"]) & {
    display: block;
  }
`;

const AutoDarkVisual = styled(Visual)`
  body[data-theme-source="sorbet-auto"][data-theme-type="dark"] & {
    display: block;
  }
`;

const AmberDarkVisual = styled(Visual)`
  body[data-theme="amber"] & {
    display: block;
  }
`;

const ForestDarkVisual = styled(Visual)`
  body[data-theme="forest"] & {
    display: block;
  }
`;

const AuroraDarkVisual = styled(Visual)`
  body[data-theme="aurora-borealis"] & {
    display: block;
  }
`;

const CauldronDarkVisual = styled(Visual)`
  body[data-theme="cauldron"] & {
    display: block;
  }
`;

const CharcoalDarkVisual = styled(Visual)`
  body[data-theme="charcoal"]:not([data-theme-source="sorbet-auto"]) & {
    display: block;
  }

  body[data-theme-type="dark"]:not([data-theme]) & {
    display: block;
  }
`;

const ScreenReaderContent = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const OverlayControl = styled.button`
  position: absolute;
  box-sizing: border-box;
  z-index: 1;
  display: block;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: transparent !important;
  cursor: pointer;
  font-size: 0;
  line-height: 0;
  margin: 0;
  padding: 0 !important;
  appearance: none;
  transition-property: transform;
  transition-duration: 140ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);

  &::before,
  &::after {
    content: none !important;
    display: none !important;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-cta-high-contrast, #8e08e4);
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.96);
  }
`;

const CloseButton = styled(OverlayControl)`
  top: 12px;
  right: 16px;
  width: 40px;
  height: 40px;

  &:focus,
  &:focus-visible {
    outline: 0 !important;
    box-shadow: none !important;
  }
`;

const LearnMoreLink = styled(OverlayControl).attrs({ as: "a" })`
  top: 216px;
  left: 80px;
  width: 122px;
  height: 40px;
  text-decoration: none;
`;

const UpgradeButton = styled(OverlayControl)`
  top: 216px;
  left: 210px;
  width: 136px;
  height: 40px;
`;

/**
 * Upsell shown when manuscript Feedback is gated by plan (Hobby & Student / no paid Pro tier access).
 *
 * Rendered inside the Feedback tab `<li>` so it auto-anchors to the button using the
 * same `position: absolute; top: calc(100% + 8px); left: 0` pattern as FeedbackTypeSelector.
 */
export function FeedbackPlanUpsellDialog({ open, onOpenChange, source, currentPlanTitle }: FeedbackPlanUpsellDialogProps) {
  const history = useHistory();
  const panelRef = useRef<HTMLDivElement>(null);
  const analyticsProps = useMemo(() => ({ feedbackUpsellSource: source, currentPlanTitle }), [source, currentPlanTitle]);

  useEffect(() => {
    if (!open) return;
    AppEventPublisher.feedback.planUpsellViewed(analyticsProps);
  }, [analyticsProps, open]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const targetEl = target as Element;
      if ((targetEl as Element).closest?.(".open-wormhole") || (targetEl as Element).closest?.(".chevron")) {
        return;
      }
      if (panelRef.current && !panelRef.current.contains(target)) {
        onOpenChange(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <UpsellPanel
      ref={panelRef}
      className="feedback-plan-upsell"
      role="dialog"
      aria-modal="false"
      aria-label="Upgrade to unlock Feedback"
    >
      <LightVisual
        src="/assets/feedback-upsell-dialog.png"
        alt=""
        aria-hidden="true"
      />
      <AutoDarkVisual
        src="/assets/feedback-upsell-dialog-dark-auto.png"
        alt=""
        aria-hidden="true"
      />
      <AmberDarkVisual
        src="/assets/feedback-upsell-dialog-dark-amber.png"
        alt=""
        aria-hidden="true"
      />
      <ForestDarkVisual
        src="/assets/feedback-upsell-dialog-dark-forest.png"
        alt=""
        aria-hidden="true"
      />
      <AuroraDarkVisual
        src="/assets/feedback-upsell-dialog-dark-aurora-borealis.png"
        alt=""
        aria-hidden="true"
      />
      <CauldronDarkVisual
        src="/assets/feedback-upsell-dialog-dark-cauldron.png"
        alt=""
        aria-hidden="true"
      />
      <CharcoalDarkVisual
        src="/assets/feedback-upsell-dialog-dark-charcoal.png"
        alt=""
        aria-hidden="true"
      />
      <ScreenReaderContent>
        <h2 id="feedback-plan-upsell-title">
          <span>Meet</span> <span>Feedback</span>. <span>Your new</span> <span>editing</span> <span>partner</span>.
        </h2>
        <p id="feedback-plan-upsell-description">Feedback suggests where to sharpen lines, improve dialogue, and strengthen your story.</p>
        <p>Available on Pro and Max plans</p>
      </ScreenReaderContent>
      <CloseButton
        type="button"
        aria-label="Close"
        onClick={() => onOpenChange(false)}
      />
      <LearnMoreLink
        href={FEEDBACK_LEARN_MORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Learn more about Feedback"
        onClick={() => {
          AppEventPublisher.feedback.planUpsellLearnMoreClicked(analyticsProps);
        }}
      />
      <UpgradeButton
        type="button"
        onClick={() => {
          AppEventPublisher.feedback.planUpsellUpgradeClicked(analyticsProps);
          onOpenChange(false);
          history.push("/app/plans?ref=toolbar_feedback_pro");
        }}
        aria-label="Upgrade now to unlock Feedback"
      />
    </UpsellPanel>
  );
}

export default FeedbackPlanUpsellDialog;
